import type { PortalFlowV2 } from '@/features/client-portal/models/portal-form'
import { formatHumanDate } from '@/shared/utils/format-date'

/**
 * Estados terminales de un flujo del portal (espejo de PortalFormStatus del backend).
 * Incluye `rejected`: un flujo rechazado NO es un flujo activo — sin otro flujo vivo,
 * el panel cae al formulario de creación, que es el comportamiento correcto.
 */
export const TERMINAL_FLOW_STATUSES = new Set(['approved', 'rejected', 'expired', 'revoked'])

/**
 * Los estados en los que el enlace **todavía le abre el formulario al cliente**, y por lo tanto
 * tiene sentido ofrecer «copiar enlace».
 *
 * ☠️ **No es «los que no son terminales».** Los tres de revisión —`submitted`, `under_review`,
 * `resubmitted`— tampoco son compartibles: el cliente ya entregó y el formulario está en lectura,
 * así que el enlace abre una pantalla donde no puede hacer nada. Y `draft` todavía no mandó nada.
 *
 * ⚠️ **El servidor es la autoridad, no esta lista.** `canSharePortalLink` en
 * `v2/portal/domain/portal-transition-policy.ts` decide de verdad y responde `409
 * PORTAL_LINK_NOT_SHAREABLE`. Esto sólo evita ofrecer un botón que va a fallar — si las dos
 * discrepan, gana el servidor y el usuario ve el error traducido.
 */
export const SHAREABLE_FLOW_STATUSES = new Set([
  'sent',
  'opened',
  'in_progress',
  'changes_requested'
])

/** Fecha legible ("14 de febrero, 2026") a partir de un ISO. */
export function formatFlowDate(iso?: string | null): string {
  return formatHumanDate(iso)
}

/** Copia ordenada por `createdAt` descendente (el backend devuelve la lista sin ordenar). */
export function sortByCreatedAtDesc(flows: PortalFlowV2[]): PortalFlowV2[] {
  return [...flows].sort(
    (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
  )
}

/**
 * El flujo "actual" del expediente: el no-terminal más reciente. Si no hay, el expirado
 * más reciente (para poder renovar su enlace en lugar de mostrar "sin flujo").
 */
export function selectCurrentFlow(flows: PortalFlowV2[]): PortalFlowV2 | undefined {
  const sorted = sortByCreatedAtDesc(flows)
  return (
    sorted.find((f) => !TERMINAL_FLOW_STATUSES.has(f.status)) ??
    sorted.find((f) => f.status === 'expired')
  )
}

/**
 * Flujos "anteriores" = complemento de `selectCurrentFlow`, orden descendente. Definirlo
 * como complemento garantiza que ningún flujo se muestre dos veces ni se pierda, incluso
 * cuando no hay flujo actual (todos terminales no expirados → todos son anteriores).
 */
export function selectPreviousFlows(flows: PortalFlowV2[]): PortalFlowV2[] {
  const current = selectCurrentFlow(flows)
  return sortByCreatedAtDesc(flows).filter((f) => f.id !== current?.id)
}
